/**
 * Live data transformer.
 *
 * Converts real clusters + signals from the AURA gateway
 * into the SyntheticField format the 3D renderer expects.
 *
 * No hardcoded categories, no editorial positions.
 * The DB clusters ARE the organizing principle.
 */

import type { SyntheticField, SyntheticNode, SyntheticEdge } from './synthetic';
import type { FieldData } from '../api/client';

// ── Temporal decay ────────────────────────────────────────────────────────────
const DECAY_HALF_LIFE_MS = 7 * 24 * 60 * 60 * 1000;
const DECAY_LAMBDA = Math.LN2 / DECAY_HALF_LIFE_MS;
const MIN_INTENSITY = 0.12;

function temporalDecay(updatedAt: number): number {
  const age = Date.now() - updatedAt;
  if (age <= 0) return 1.0;
  return Math.max(MIN_INTENSITY, Math.exp(-DECAY_LAMBDA * age));
}

// ── Seeded random ─────────────────────────────────────────────────────────────
function seededRandom(seed: number): () => number {
  let s = seed;
  return () => {
    s = (s * 1103515245 + 12345) & 0x7fffffff;
    return s / 0x7fffffff;
  };
}

// ── Color from topic ──────────────────────────────────────────────────────────
// Hash topic string → stable hue. No hardcoded color map.
function topicToHue(topic: string): number {
  let h = 0;
  for (let i = 0; i < topic.length; i++) {
    h = (h * 31 + topic.charCodeAt(i)) & 0xffffffff;
  }
  return Math.abs(h % 1000) / 1000;
}

function hslToRgb(h: number, s: number, l: number): [number, number, number] {
  let r: number, g: number, b: number;
  if (s === 0) {
    r = g = b = l;
  } else {
    const hue2rgb = (p: number, q: number, t: number) => {
      if (t < 0) t += 1;
      if (t > 1) t -= 1;
      if (t < 1 / 6) return p + (q - p) * 6 * t;
      if (t < 1 / 2) return q;
      if (t < 2 / 3) return p + (q - p) * (2 / 3 - t) * 6;
      return p;
    };
    const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
    const p = 2 * l - q;
    r = hue2rgb(p, q, h + 1 / 3);
    g = hue2rgb(p, q, h);
    b = hue2rgb(p, q, h - 1 / 3);
  }
  return [r, g, b];
}

// ── Cluster positioning ───────────────────────────────────────────────────────
// Golden angle spiral — evenly distributes N clusters across a sphere.
// High-confidence clusters pulled toward center; decayed ones pushed out.
function clusterPosition(
  index: number,
  total: number,
  confidence: number,
  decay: number,
  seed: number
): [number, number, number] {
  const rand = seededRandom(seed);
  const jitter = () => (rand() - 0.5) * 40;

  const GOLDEN = Math.PI * (3 - Math.sqrt(5)); // golden angle ≈ 137.5°
  const y = 1 - (index / Math.max(total - 1, 1)) * 2; // -1 → +1
  const radius = Math.sqrt(1 - y * y);
  const theta = GOLDEN * index;

  // Base spread: strong clusters closer to center
  const spread = 180 + (1 - confidence) * 120 + (1 - decay) * 80;

  return [
    Math.cos(theta) * radius * spread + jitter(),
    y * spread + jitter(),
    Math.sin(theta) * radius * spread + jitter(),
  ];
}

// ── Entity overlap: how similar are two clusters ──────────────────────────────
function entityOverlap(a: string[], b: string[]): number {
  const setB = new Set(b.map(s => s.toLowerCase()));
  return a.filter(e => setB.has(e.toLowerCase())).length;
}

// ── Main transformer ──────────────────────────────────────────────────────────

export function transformLiveData(data: FieldData): SyntheticField {
  const nodes: SyntheticNode[] = [];
  const edges: SyntheticEdge[] = [];

  const clusters: any[] = data.clusters || [];
  const signals: any[] = data.signals || [];

  // ── Build nodes from DB clusters ──────────────────────────────────────────
  if (clusters.length > 0) {
    const clusterNodeIndex = new Map<string, number>(); // clusterId → node index

    clusters.forEach((cluster: any, idx: number) => {
      const decay = temporalDecay(cluster.updated_at || cluster.created_at || Date.now());
      const confidence = (cluster.confidence ?? 0.5) * decay;

      const hue = topicToHue(cluster.topic);
      const color = hslToRgb(hue, 0.80, 0.38 + confidence * 0.20);
      const position = clusterPosition(idx, clusters.length, cluster.confidence ?? 0.5, decay, idx * 9973);

      const signalCount = (cluster.signalIds ?? []).length;
      const trend: 'rising' | 'stable' | 'fading' =
        decay < 0.3 ? 'fading' : signalCount >= 8 ? 'rising' : 'stable';

      clusterNodeIndex.set(cluster.id, nodes.length);

      nodes.push({
        id: cluster.id,
        name: cluster.topic,
        category: cluster.topic, // topic IS the category now
        position,
        scale: 0.5 + confidence * 1.8,
        color,
        intensity: confidence,
        velocity: (topicToHue(cluster.topic + 'v') - 0.5) * 2,
        trend,
        flags: 0,
      });

      // Satellite nodes — visual gravitational well effect.
      // Named after parent cluster (not entity strings) so no noise labels appear.
      const entityCount = Math.min((cluster.entities ?? []).length, 5);
      const rand = seededRandom(idx * 1337);
      for (let s = 0; s < entityCount; s++) {
        const spread = 35 + (1 - confidence) * 25;
        const satPos: [number, number, number] = [
          position[0] + (rand() - 0.5) * spread,
          position[1] + (rand() - 0.5) * spread,
          position[2] + (rand() - 0.5) * spread,
        ];
        const satIntensity = 0.25 + rand() * 0.35;
        const satColor = hslToRgb((hue + 0.03 * (rand() - 0.5) + 1) % 1, 0.72, 0.32 + satIntensity * 0.15);

        const satIdx = nodes.length;
        nodes.push({
          id: `${cluster.id}-e${s}`,
          name: cluster.topic, // use cluster topic, not entity string — prevents noise labels
          category: cluster.topic,
          position: satPos,
          scale: 0.20 + satIntensity * 0.45,
          color: satColor,
          intensity: satIntensity,
          velocity: (rand() - 0.5) * 1.5,
          trend: 'stable',
          flags: 0,
        });

        edges.push({ source: satIdx, target: clusterNodeIndex.get(cluster.id)!, weight: 0.4 + rand() * 0.4 });
      }
    });

    // Cross-cluster edges based on entity overlap (not hardcoded categories)
    for (let i = 0; i < clusters.length; i++) {
      for (let j = i + 1; j < clusters.length; j++) {
        const overlap = entityOverlap(clusters[i].entities ?? [], clusters[j].entities ?? []);
        if (overlap > 0) {
          const iIdx = clusterNodeIndex.get(clusters[i].id);
          const jIdx = clusterNodeIndex.get(clusters[j].id);
          if (iIdx !== undefined && jIdx !== undefined) {
            edges.push({
              source: iIdx,
              target: jIdx,
              weight: Math.min(overlap / 4, 1.0),
            });
          }
        }
      }
    }

    // Distance-based edges for clusters with no entity overlap but close positions
    for (let i = 0; i < clusters.length; i++) {
      for (let j = i + 1; j < clusters.length; j++) {
        const ni = nodes[clusterNodeIndex.get(clusters[i].id)!];
        const nj = nodes[clusterNodeIndex.get(clusters[j].id)!];
        if (!ni || !nj) continue;
        const dx = ni.position[0] - nj.position[0];
        const dy = ni.position[1] - nj.position[1];
        const dz = ni.position[2] - nj.position[2];
        const dist = Math.sqrt(dx * dx + dy * dy + dz * dz);
        if (dist < 120) {
          const rand = seededRandom(i * 9999 + j);
          if (rand() > 0.5) {
            edges.push({ source: clusterNodeIndex.get(clusters[i].id)!, target: clusterNodeIndex.get(clusters[j].id)!, weight: 0.15 });
          }
        }
      }
    }
  }

  // ── Orphan signals (no cluster) as faint ambient nodes ────────────────────
  if (nodes.length === 0 && signals.length > 0) {
    // Only if we have no clusters at all — show raw signals
    const rand = seededRandom(42);
    signals.slice(0, 100).forEach((sig: any, idx: number) => {
      const hue = topicToHue(sig.content?.slice(0, 20) ?? '');
      const decay = temporalDecay(sig.updated_at || sig.created_at || Date.now());
      nodes.push({
        id: sig.id,
        name: sig.content?.slice(0, 40) ?? `Signal ${idx}`,
        category: sig.source ?? 'SERP',
        position: [
          (rand() - 0.5) * 400,
          (rand() - 0.5) * 400,
          (rand() - 0.5) * 400,
        ],
        scale: 0.2 + (sig.confidence ?? 0.5) * 0.4,
        color: hslToRgb(hue, 0.7, 0.35),
        intensity: (sig.confidence ?? 0.5) * decay,
        velocity: (rand() - 0.5),
        trend: 'stable',
        flags: 0,
      });
    });
  }

  console.log(`[LIVE] ${clusters.length} clusters + ${signals.length} signals → ${nodes.length} nodes, ${edges.length} edges`);
  return { nodes, edges };
}
